/**
 * Artist Influencer Discount Program.
 *
 * A super admin flags a brand as an influencer and sets its materials-only
 * discount percentage. The influencer maintains a roster of invitee emails;
 * when a rostered email signs up as an artist, that new brand permanently
 * inherits the influencer's discount on internal self-orders.
 */

export enum InfluencerMemberStatus {
    INVITED = 'INVITED',
    SIGNED_UP = 'SIGNED_UP'
}

export enum ArtistInfluencerErrorCode {
    NOT_AN_INFLUENCER = 'NOT_AN_INFLUENCER',
    EMAIL_ON_ANOTHER_ROSTER = 'EMAIL_ON_ANOTHER_ROSTER',
    INVALID_EMAIL = 'INVALID_EMAIL',
    INVALID_DISCOUNT = 'INVALID_DISCOUNT',
    INVALID_PAYLOAD = 'INVALID_PAYLOAD',
    MEMBER_NOT_FOUND = 'MEMBER_NOT_FOUND',
    INTERNAL_ERROR = 'INTERNAL_ERROR'
}

export interface IArtistInfluencer {
    id: number;
    brand_id: number;
    brand_name: string;
    discount_percent: number;
    is_active: boolean;
    roster_count: number;
    linked_count: number;
    created_at?: string;
}

export interface IInfluencerMember {
    id: number;
    influencer_id: number;
    email: string;
    first_name: string;
    last_name: string;
    status: InfluencerMemberStatus;
    invited_at?: string | null;
    linked_brand_id?: number | null;
    linked_at?: string | null;
    is_active: boolean;
    created_at?: string;
}

export interface IInfluencerMemberInput {
    email: string;
    first_name: string;
    last_name: string;
}

export interface IBulkAddRowError {
    row: number;
    email: string;
    reason: string;
}

export interface IBulkAddResult {
    added: number;
    skipped: number;
    errors: IBulkAddRowError[];
}

export interface IInfluencerMe {
    is_influencer: boolean;
    influencer_id?: number;
    discount_percent?: number;
}

export interface IBrandSearchResult {
    id: number;
    name: string;
}

/** Typed failure shape; callers narrow with `isInfluencerError`. */
export interface IArtistInfluencerError {
    ok: false;
    code: ArtistInfluencerErrorCode;
    message: string;
    status: number;
}

export const isInfluencerError = (value: any): value is IArtistInfluencerError =>
    !!value && typeof value === 'object' && value.ok === false && typeof value.code === 'string';

/** Max rows accepted in one CSV upload — enforced by the backend too. */
export const INFLUENCER_BULK_ROW_LIMIT = 1000;
